/**
 * This code is copied from packages/design-system/navigation-system/src/ui/menu-item/menu-item.tsx
 */
import React, { type PropsWithChildren } from 'react';

import { cssMap, cx, keyframes } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { LazyDragHandle } from '@atlaskit/side-nav-items/drag-and-drop/drag-handle';

// Note: this is also used in `drag-handle.tsx`
const dragHandleDisplayVar = '--drag-handle-display';

const dragCursorAnimation = keyframes({
	to: {
		cursor: 'grab',
	},
});

const topLevelSiblingStyles = cssMap({
	root: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
	},
});

const styles = cssMap({
	// Only applying on interactive element as we are not enabling
	// dragging from nested interactive elements
	hasDragIndicatorOld: {
		/**
		 * Opting out of long press actions on iOS as they don't work well with drag and drop.
		 *
		 * If you don't opt out, anchors can get a context menu including a link preview and actions
		 * (eg "open in new tab"). While it's still possible to do drag and drop operations with this
		 * context menu, it's a pain as you need to close the context menu while keeping your dragging
		 * finger depressed, then you can continue with the drag operation.
		 *
		 * TODO: could consider adding this to all menu items for a consistent experience.
		 */
		'-webkit-touch-callout': 'none',
		// change cursor on hover
		[dragHandleDisplayVar]: 'none',

		'&:hover': {
			[dragHandleDisplayVar]: 'flex',
			animationName: dragCursorAnimation,

			// instant animation
			animationDuration: '0s',

			// delay cursor change
			animationDelay: '800ms',

			// keep the end state when the animation ends
			animationFillMode: 'forwards',
		},
	},
	hasDragIndicator: {
		/**
		 * Opting out of long press actions on iOS as they don't work well with drag and drop.
		 *
		 * If you don't opt out, anchors can get a context menu including a link preview and actions
		 * (eg "open in new tab"). While it's still possible to do drag and drop operations with this
		 * context menu, it's a pain as you need to close the context menu while keeping your dragging
		 * finger depressed, then you can continue with the drag operation.
		 *
		 * TODO: could consider adding this to all menu items for a consistent experience.
		 */
		'-webkit-touch-callout': 'none',
		// change cursor on hover
		[dragHandleDisplayVar]: 'none',

		// We check that there is actually a draggable element inside
		// to protect against incomplete implementation of DND props in custom items by consuming apps
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:has([draggable]):hover': {
			[dragHandleDisplayVar]: 'flex',
			animationName: dragCursorAnimation,

			// instant animation
			animationDuration: '0s',

			// delay cursor change
			animationDelay: '800ms',

			// keep the end state when the animation ends
			animationFillMode: 'forwards',
		},
	},
});

type Props = PropsWithChildren<{ hasDragIndicator: boolean }>;

export function DragIndicatorWrapper({ children, hasDragIndicator }: Props) {
	if (hasDragIndicator) {
		return (
			<Box
				xcss={cx(
					topLevelSiblingStyles.root,
					fg('navx-3539-custom-shortcut-component-draggable')
						? styles.hasDragIndicator
						: styles.hasDragIndicatorOld,
				)}
			>
				{children}
				{/**
				 * `LazyDragHandle` internally defers rendering until after mount to avoid
				 * hydration mismatches. It also wraps the lazy component in its own `Suspense`
				 * boundary, so its loading won't block the rendering of the rest of the menu item.
				 */}
				<LazyDragHandle />
			</Box>
		);
	}
	return <>{children}</>;
}
